import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { PageHeader } from '../components/ui/PageHeader';
import { ArgusReviewPanel } from '../components/ArgusReviewPanel';
import { PreviewEnvironmentPanel } from '../components/PreviewEnvironmentPanel';
import { Markdown } from '../components/ui/Markdown';
import { useApi } from '../hooks/useApi';
import { useRefetchOnFocus } from '../hooks/useRefetchOnFocus';
import { EmptyState } from '../components/ui/EmptyState';
import type { AthenaInitiative } from '@pantheon/shared';

const outcomeConfig: Record<string, { text: string; color: string }> = {
  in_progress: { text: 'Reviewing', color: 'bg-blue-50 text-blue-600' },
  passed: { text: 'Review passed', color: 'bg-success-light text-success' },
  declined: { text: 'Declined', color: 'bg-gray-100 text-gray-500' },
  capped: { text: 'Needs a human', color: 'bg-amber-50 text-amber-700' },
};

/**
 * Argus — the Watchman. A quality-review home: pick a fanned-out initiative and
 * run / follow its self-correcting review loop (the same panel that lives on the
 * Athena initiative page).
 */
export function ArgusPage() {
  const { get } = useApi();
  const { initiativeId: urlInitiativeId } = useParams();
  const navigate = useNavigate();
  const [initiatives, setInitiatives] = useState<AthenaInitiative[]>([]);
  const [active, setActive] = useState<AthenaInitiative | null>(null);
  const getRef = useRef(get);
  getRef.current = get;

  const fetchList = useCallback(async () => {
    const result = await getRef.current<AthenaInitiative[]>('/athena/initiatives');
    if (result.success && result.data) {
      const list = result.data;
      setInitiatives(list);
      setActive((prev) => (prev ? list.find((i) => i.initiativeId === prev.initiativeId) ?? prev : prev));
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);
  useRefetchOnFocus(fetchList);

  // Load the initiative named in the URL so /argus/:id is a shareable deep link.
  const loadedIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!urlInitiativeId) { loadedIdRef.current = null; return; }
    if (urlInitiativeId === loadedIdRef.current) return;
    loadedIdRef.current = urlInitiativeId;
    void getRef.current<AthenaInitiative>(`/athena/initiatives/${urlInitiativeId}`).then((r) => {
      if (r.success && r.data) setActive(r.data);
    });
  }, [urlInitiativeId]);

  const openInitiative = useCallback((init: AthenaInitiative) => {
    setActive(init);
    loadedIdRef.current = init.initiativeId;
    navigate(`/argus/${init.initiativeId}`);
  }, [navigate]);

  const closeInitiative = useCallback(() => {
    setActive(null);
    loadedIdRef.current = null;
    navigate('/argus');
    fetchList();
  }, [navigate, fetchList]);

  return (
    <div className="max-w-5xl animate-rise">
      <PageHeader
        mark="A"
        markBg="bg-argus/12"
        markText="text-argus"
        title="Argus"
        subtitle={
          <>
            The watchman. Argus reviews a finished feature by <b>running it</b> — boots the app on the initiative's
            integration branch, walks the acceptance criteria with accessibility and workflow checks, and loops any
            fixes back onto the plan for approval, re-reviewing until the feature is clean.
          </>
        }
      />

      {!active && (
        <>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Initiatives</h3>
          {initiatives.length === 0 ? (
            <EmptyState title="No initiatives yet" hint="Fan one out on the Athena page, then review it here." />
          ) : (
            <div className="space-y-2">
              {initiatives.map((init) => {
                const oc = init.reviewLoop?.outcome ? outcomeConfig[init.reviewLoop.outcome] : null;
                const reviewable = !!init.integrationBranch;
                return (
                  <div
                    key={init.initiativeId}
                    onClick={() => openInitiative(init)}
                    className="bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 p-4 cursor-pointer hover:shadow-md hover:shadow-slate-200/60 transition-shadow flex items-center justify-between gap-3"
                  >
                    <span className="text-sm text-gray-700 truncate">{init.brief.substring(0, 120)}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      {init.reviewLoop?.iteration ? (
                        <span className="text-xs text-gray-500">iteration {init.reviewLoop.iteration}</span>
                      ) : null}
                      {oc ? (
                        <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${oc.color}`}>{oc.text}</span>
                      ) : (
                        <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${reviewable ? 'bg-primary-light text-primary' : 'bg-gray-100 text-gray-500'}`}>
                          {reviewable ? 'Ready to review' : 'Not fanned out'}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {active && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Reviewing initiative</h3>
            <Button variant="ghost" size="sm" onClick={closeInitiative}>Back</Button>
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 p-5">
            <Markdown>{active.brief}</Markdown>
          </div>
          {active.previewEnvironment && (
            <PreviewEnvironmentPanel
              initiativeId={active.initiativeId}
              preview={active.previewEnvironment}
              onChange={setActive}
            />
          )}
          <ArgusReviewPanel
            initiativeId={active.initiativeId}
            canReview={!!active.integrationBranch}
            loopOutcome={active.reviewLoop?.outcome}
          />
        </div>
      )}
    </div>
  );
}
